import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { fillRouteWithSlugs } from "@utils";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "src/components";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { TrainingQuizAdminSection } from "src/components/Trainings/TrainingQuizAdminSection";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { useGetUser } from "src/hooks";
import { useGetTaskProgramDetail } from "src/hooks/queries/task-program-mutations";
import { EntitlementType } from "src/stores/models";
import { hasFunctionalGroupEntitlement } from "src/utils/entitlements";

export const TrainingQuizEdit = () => {
  const { orgId = "", programId = "" } = useParams();
  const navigate = useNavigate();
  const { data: user, isLoading: isLoadingUser } = useGetUser();
  const { data, isLoading, isError } = useGetTaskProgramDetail(
    orgId,
    programId
  );

  const trainingsHref = fillRouteWithSlugs(ROUTES.TRAININGS, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
  });
  const trainingDetailHref = fillRouteWithSlugs(ROUTES.TRAINING_DETAIL, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
    [ROUTE_SLUGS.PROGRAM_ID]: programId,
  });
  const trainingEditHref = fillRouteWithSlugs(ROUTES.TRAINING_EDIT, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
    [ROUTE_SLUGS.PROGRAM_ID]: programId,
  });

  const canAdministerAllTrainings =
    !!user?.permissions?.includes("write:configuration") ||
    hasFunctionalGroupEntitlement(
      user,
      orgId,
      EntitlementType.ADMINISTER_ALL_TRAININGS
    );

  const pageHeader = (programName = "…") => (
    <PageHeader
      pageTitleComponent={
        <PageBreadcrumb
          items={[
            { label: "Training Programs", to: trainingsHref },
            { label: programName, to: trainingDetailHref },
            { label: "Edit Quiz" },
          ]}
        />
      }
    />
  );

  if (isError) {
    return (
      <Box className="flex flex-col gap-4">
        {pageHeader()}
        <Box className="flex flex-1 flex-col items-center justify-center gap-2 py-8 px-3">
          <Typography color="error">Training program not found</Typography>
          <Button onClick={() => navigate(trainingsHref)}>
            Back to Trainings
          </Button>
        </Box>
      </Box>
    );
  }

  if (isLoading || isLoadingUser || !data) {
    return (
      <Box className="flex flex-col gap-4">
        {pageHeader()}
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  const program = data;

  if (!canAdministerAllTrainings) {
    return (
      <Box className="flex flex-col gap-4">
        {pageHeader(program.name)}
        <Box className="flex flex-1 flex-col items-center justify-center gap-2 py-8 px-3">
          <Typography color="error">
            You do not have permission to edit this quiz.
          </Typography>
          <Button onClick={() => navigate(trainingDetailHref)}>
            Back to training
          </Button>
        </Box>
      </Box>
    );
  }

  const currentLiveRevision = program.fireableRevisions?.[0] ?? null;
  const quizTargetRevision = program.draftRevision ?? currentLiveRevision;
  const isQuizTargetLive = !program.draftRevision;
  const quizTargetOccurrence = quizTargetRevision
    ? (program.occurrences.find(
        (occurrence) =>
          occurrence.documentVersion?.id ===
          quizTargetRevision.documentVersionId
      ) ?? null)
    : null;
  const quizTargetWasSentWithoutQuiz =
    !!quizTargetOccurrence && !quizTargetOccurrence.quizId;
  const quizTargetHasCompletedTraining =
    quizTargetOccurrence?.tasks.some(
      (task) => task.manualStatus === "DONE" || !!task.trainingSignedAt
    ) ?? false;
  const canAdministerQuizTarget =
    !!quizTargetRevision &&
    (!quizTargetWasSentWithoutQuiz || !quizTargetHasCompletedTraining);
  const historicalQuizRevisions = (program.fireableRevisions ?? []).filter(
    (item) => item.documentVersionId !== quizTargetRevision?.documentVersionId
  );

  let unavailableMessage: string | null = null;
  if (program.completionMethod !== "QUIZ") {
    unavailableMessage =
      "This training uses Read and sign. Change its completion method to Quiz and sign before editing a quiz.";
  } else if (quizTargetWasSentWithoutQuiz) {
    unavailableMessage =
      "This revision was already sent as read-and-sign training. The quiz method will apply to the next published revision.";
  } else if (!quizTargetRevision) {
    unavailableMessage =
      "Create a document draft before drafting its training quiz.";
  }

  return (
    <Box className="flex flex-col gap-4">
      {pageHeader(program.name)}
      <Box className="w-full px-3 pb-6">
        <Box className="mx-auto w-full max-w-[800px]">
          <Typography variant="h5" sx={{ mb: 2 }}>
            Edit quiz
          </Typography>

          {program.completionMethod === "QUIZ" &&
          quizTargetRevision &&
          canAdministerQuizTarget ? (
            <TrainingQuizAdminSection
              orgId={orgId}
              programId={program.id}
              documentId={program.linkedDocument.id}
              documentVersionId={quizTargetRevision.documentVersionId}
              revision={quizTargetRevision.revision}
              isRevisionLive={isQuizTargetLive}
              historicalQuizRevisions={historicalQuizRevisions}
            />
          ) : (
            <Alert
              severity="info"
              action={
                program.completionMethod !== "QUIZ" ? (
                  <Button component={RouterLink} to={trainingEditHref}>
                    Edit training
                  </Button>
                ) : undefined
              }
            >
              {unavailableMessage}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  );
};
